// ═══════════════════════════════════════════════════════════════════════════════
// I Ching Numerology Framework — Life Manual Method
// by Alex Loh
// Trader Cognitive Bias Diagnosis — Step 1
//
// ALL formulas, mappings, and logic derived STRICTLY from the I Ching
// Numerology Framework knowledge base. No assumptions from other systems.
// ═══════════════════════════════════════════════════════════════════════════════

export interface PersonInput {
  name: string;
  dob: string; // YYYY-MM-DD
}

// ─── Core Reduction ────────────────────────────────────────────────────────────
// Reduce any number to a single digit 1–9 by summing digits repeatedly.
// 0 stays 0 (special case for year "00").
function reduce(n: number): number {
  if (n === 0) return 0;
  while (n > 9) {
    n = String(n)
      .split('')
      .reduce((s, d) => s + parseInt(d, 10), 0);
  }
  return n;
}

// ─── Parse DOB into A, B, D, E ─────────────────────────────────────────────────
// Input: YYYY-MM-DD
// A = Day (reduced), B = Month (reduced), D = Century (reduced), E = Year (reduced)
function parseDOB(dob: string): { A: number; B: number; D: number; E: number } {
  const [yearStr, monthStr, dayStr] = dob.split('-');
  const day = parseInt(dayStr, 10);
  const month = parseInt(monthStr, 10);
  const century = parseInt(yearStr.slice(0, 2), 10);
  const yearPart = parseInt(yearStr.slice(2), 10); // last 2 digits of year

  return {
    A: reduce(day),
    B: reduce(month),
    D: reduce(century),
    E: yearPart === 0 ? 0 : reduce(yearPart),
  };
}

// ─── All 19 Positions ──────────────────────────────────────────────────────────
export interface IChingPositions {
  // Layer 1 — from birth date
  A: number; B: number; D: number; E: number;
  // Layer 2 — triangle core
  C: number; F: number; G: number;
  // Layer 3 — extended positions
  H: number; I: number; J: number;
  K: number; L: number; M: number;
  N: number; O: number; P: number;
  // Hidden potential
  Q: number; R: number; S: number;
  // Destined dream
  destinedDream: number;
}

export function buildPositions(dob: string): IChingPositions {
  const { A, B, D, E } = parseDOB(dob);

  // Layer 2
  const C = reduce(A + B);
  const F = reduce(D + E);
  const G = reduce(C + F);

  // Layer 3
  const H = reduce(C + G);
  const I = reduce(F + G);
  const J = reduce(I + H);
  const K = reduce(A + C);
  const L = reduce(B + C);
  const M = reduce(K + L);
  const N = reduce(D + F);
  const O = reduce(E + F);
  const P = reduce(N + O);

  // Hidden potential
  const Q = reduce(C + C);
  const R = reduce(F + F);
  const S = reduce(G + G);

  // Destined dream
  const destinedDream = reduce(A + E + G);

  return { A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, destinedDream };
}

// ─── 13 Behaviour Codes ────────────────────────────────────────────────────────
// Each code = Seed + Process + Fruit (three positions concatenated)
export interface BehaviourCode {
  id: number;
  name: string;
  lifeArea: string;
  code: string;   // e.g. "145"
  seed: number;
  process: number;
  fruit: number;
}

function makeCode(
  seed: number,
  process: number,
  fruit: number,
  id: number,
  name: string,
  lifeArea: string
): BehaviourCode {
  return { id, name, lifeArea, code: `${seed}${process}${fruit}`, seed, process, fruit };
}

export function buildBehaviourCodes(p: IChingPositions): BehaviourCode[] {
  return [
    makeCode(p.A, p.B, p.C,  1,  'Father Gene / Career',              'Father influence & career foundation'),
    makeCode(p.D, p.E, p.F,  2,  'Mother Gene / Marriage',            'Mother influence & marriage foundation'),
    makeCode(p.C, p.F, p.G,  3,  'Main Character / Self',             'Core personality & self'),
    makeCode(p.C, p.G, p.H,  4,  'Life Journey (Father Side)',        'Life path — father side'),
    makeCode(p.F, p.G, p.I,  5,  'Life Journey (Mother Side)',        'Life path — mother side'),
    makeCode(p.I, p.H, p.J,  6,  'Children / Subordinates',           'Children, subordinates, people under you'),
    makeCode(p.A, p.C, p.K,  7,  'Career Journey 1',                  'Career path 1'),
    makeCode(p.B, p.C, p.L,  8,  'Career Journey 2',                  'Career path 2'),
    makeCode(p.K, p.L, p.M,  9,  'Current Friends',                   'Current social circle & peers'),
    makeCode(p.D, p.F, p.N,  10, 'Marriage Journey 1',                'Marriage path 1'),
    makeCode(p.E, p.F, p.O,  11, 'Marriage Journey 2',                'Marriage path 2'),
    makeCode(p.N, p.O, p.P,  12, 'Future Wealth / Health',            'Future wealth, health & in-laws'),
    makeCode(p.Q, p.R, p.S,  13, 'Hidden Potential / Subconscious',   'Subconscious patterns & hidden potential'),
  ];
}

// ─── Number → Element Mapping ──────────────────────────────────────────────────
// Per I Ching framework (knowledge base):
// 1, 6 = Metal | 2, 7 = Water | 3, 8 = Fire | 4, 9 = Wood | 5 = Earth
const NUMBER_TO_ELEMENT: Record<number, string> = {
  1: 'Metal', 6: 'Metal',
  2: 'Water', 7: 'Water',
  3: 'Fire',  8: 'Fire',
  4: 'Wood',  9: 'Wood',
  5: 'Earth',
};

// ─── 5 Elements Box ────────────────────────────────────────────────────────────
// Count positions A–P (16 positions). Q, R, S are NOT counted.
// Column order is DYNAMIC — determined by G's element, following the
// Generating Cycle: Wood → Fire → Earth → Metal → Water → (back to Wood)
//
// Column meanings (fixed):
//   Col 0: Self
//   Col 1: Children / Wealth
//   Col 2: Career / Partner
//   Col 3: Official / Illness
//   Col 4: Parents / Benefactor
//
// Benchmark: 4 : 3 : 3 : 3 : 3 (total = 16)

const GENERATING_CYCLE: string[] = ['Wood', 'Fire', 'Earth', 'Metal', 'Water'];

function getElementOfNumber(n: number): string {
  return NUMBER_TO_ELEMENT[n] || 'Unknown';
}

function getColumnOrder(gValue: number): string[] {
  // Start from G's element and follow the generating cycle
  const gElement = getElementOfNumber(gValue);
  const startIdx = GENERATING_CYCLE.indexOf(gElement);
  if (startIdx === -1) return GENERATING_CYCLE; // fallback
  const order: string[] = [];
  for (let i = 0; i < 5; i++) {
    order.push(GENERATING_CYCLE[(startIdx + i) % 5]);
  }
  return order;
}

export interface FiveElementsColumn {
  label: string;   // e.g. "Self", "Children / Wealth"
  element: string; // e.g. "Metal"
  count: number;
  benchmark: number;
  status: 'excess' | 'balanced' | 'deficient';
}

export interface FiveElementsBox {
  columns: FiveElementsColumn[];
  // Raw element counts (for reference)
  Metal: number;
  Water: number;
  Fire: number;
  Wood: number;
  Earth: number;
  total: number;
  // Wealth = Children/Wealth column count (col index 1)
  wealthCount: number;
  // Parents/Benefactor column count (col index 4)
  parentsBenefactorCount: number;
  wealthStatus: 'strong' | 'balanced' | 'weak';
}

const COLUMN_LABELS = [
  'Self',
  'Children / Wealth',
  'Career / Partner',
  'Official / Illness',
  'Parents / Benefactor',
];
const COLUMN_BENCHMARKS = [4, 3, 3, 3, 3];

export function buildFiveElements(p: IChingPositions): FiveElementsBox {
  // Count A–P (16 positions)
  const positions16 = [p.A, p.B, p.C, p.D, p.E, p.F, p.G, p.H, p.I, p.J, p.K, p.L, p.M, p.N, p.O, p.P];

  const rawCounts: Record<string, number> = { Metal: 0, Water: 0, Fire: 0, Wood: 0, Earth: 0 };
  positions16.forEach(n => {
    const el = NUMBER_TO_ELEMENT[n];
    if (el) rawCounts[el]++;
  });

  const total = positions16.length; // always 16

  // Dynamic column order based on G
  const colOrder = getColumnOrder(p.G);

  const columns: FiveElementsColumn[] = colOrder.map((element, idx) => {
    const count = rawCounts[element] || 0;
    const benchmark = COLUMN_BENCHMARKS[idx];
    let status: 'excess' | 'balanced' | 'deficient';
    if (count > benchmark) status = 'excess';
    else if (count < benchmark) status = 'deficient';
    else status = 'balanced';
    return { label: COLUMN_LABELS[idx], element, count, benchmark, status };
  });

  const wealthCount = columns[1].count;           // Children / Wealth column
  const parentsBenefactorCount = columns[4].count; // Parents / Benefactor column

  let wealthStatus: 'strong' | 'balanced' | 'weak';
  if (wealthCount >= 4) wealthStatus = 'strong';
  else if (wealthCount >= 3) wealthStatus = 'balanced';
  else wealthStatus = 'weak';

  return {
    columns,
    Metal: rawCounts['Metal'],
    Water: rawCounts['Water'],
    Fire: rawCounts['Fire'],
    Wood: rawCounts['Wood'],
    Earth: rawCounts['Earth'],
    total,
    wealthCount,
    parentsBenefactorCount,
    wealthStatus,
  };
}

// ─── Helper: Triangle Numbers (A–G, 7 positions) ──────────────────────────────
function getTriangle(p: IChingPositions): number[] {
  return [p.A, p.B, p.C, p.D, p.E, p.F, p.G];
}

function countInArr(n: number, arr: number[]): number {
  return arr.filter(x => x === n).length;
}

function missingFromArr(n: number, arr: number[]): boolean {
  return !arr.includes(n);
}

// ─── Emotion Flags ─────────────────────────────────────────────────────────────
export interface EmotionFlag {
  emotion: string;
  severity: 'extreme' | 'moderate' | 'mild';
  description: string;
  tradingImpact: string;
}

export function analyseEmotions(p: IChingPositions, codes: BehaviourCode[]): EmotionFlag[] {
  const flags: EmotionFlag[] = [];
  const triangle = getTriangle(p);
  const codeStrings = codes.map(c => c.code);
  const hasCode = (code: string) => codeStrings.includes(code);

  // ── FEAR ──────────────────────────────────────────────────────────────────
  // Extreme Fear: 4 appears more than once at A, E, or G AND 9 is missing from triangle
  // Fear: 4 appears more than once in triangle
  const foursInTriangle = countInArr(4, triangle);
  const foursAtAEG = [p.A, p.E, p.G].filter(x => x === 4).length;
  const missingNine = missingFromArr(9, triangle);

  if (foursAtAEG > 1 && missingNine) {
    flags.push({
      emotion: 'Extreme Fear',
      severity: 'extreme',
      description: 'Number 4 appears more than once at key positions (A, E, G) with 9 missing — deep paralysis pattern',
      tradingImpact: 'Severe hesitation, inability to execute trades, freezing at entry and exit points',
    });
  } else if (foursInTriangle > 1) {
    flags.push({
      emotion: 'Fear',
      severity: 'moderate',
      description: 'Number 4 appears more than once in triangle — tendency toward over-caution and overthinking',
      tradingImpact: 'Premature exits, over-analysis of setups, missing valid opportunities',
    });
  }

  // ── GREED ─────────────────────────────────────────────────────────────────
  // Extreme Greed: 3 appears more than once in triangle AND 6 missing AND has 336/663 in codes
  // Greed tendency: 3 appears more than once + 6 missing (but no 336/663)
  const threesInTriangle = countInArr(3, triangle);
  const missingSix = missingFromArr(6, triangle);
  const hasGreedCode = hasCode('336') || hasCode('663') || hasCode('999');

  if (threesInTriangle > 1 && missingSix && hasGreedCode) {
    flags.push({
      emotion: 'Extreme Greed',
      severity: 'extreme',
      description: 'Multiple 3s in triangle, 6 missing, and greed code (336/663/999) present — catastrophic risk pattern',
      tradingImpact: 'Catastrophic overleveraging, inability to take profit, high account blow-up risk',
    });
  } else if (threesInTriangle > 1 && missingSix) {
    flags.push({
      emotion: 'Greed',
      severity: 'moderate',
      description: 'Multiple 3s in triangle with 6 missing — wealth-seeking without discipline',
      tradingImpact: 'Overleveraging, holding winners too long, overtrading after wins',
    });
  }

  // ── EGO ───────────────────────────────────────────────────────────────────
  // Deep Ego: 156 or 516 at position 13 (QRS = Hidden Potential/Subconscious)
  // Ego: 156 or 516 in any of the 13 behaviour codes
  const code13 = codes.find(c => c.id === 13);
  const hasEgoCode = hasCode('156') || hasCode('516');
  const isDeepEgo = code13 && (code13.code === '156' || code13.code === '516');

  if (isDeepEgo) {
    flags.push({
      emotion: 'Deep Ego',
      severity: 'extreme',
      description: 'Ego code (156/516) at subconscious position (QRS) — operates below conscious awareness',
      tradingImpact: 'Subconscious refusal to accept losses, revenge trading, inability to follow rules under stress',
    });
  } else if (hasEgoCode) {
    flags.push({
      emotion: 'Ego',
      severity: 'moderate',
      description: 'Ego code (156/516) present — unwillingness to accept being wrong',
      tradingImpact: 'Refusing to cut losses, revenge trading, dismissing valid stop-loss signals',
    });
  }

  // ── IMPATIENCE ────────────────────────────────────────────────────────────
  // Pattern: 3 appears more than once in triangle AND 4 is MISSING from triangle
  // (If 4 is present, it counterbalances — impatience does NOT trigger)
  const missingFour = missingFromArr(4, triangle);

  if (threesInTriangle > 1 && missingFour) {
    flags.push({
      emotion: 'Impatience',
      severity: 'moderate',
      description: 'Multiple 3s in triangle with 4 missing — action energy without patience',
      tradingImpact: 'Forcing trades, entering before confirmation, FOMO-driven decisions',
    });
  }

  // ── EXTREME IMPATIENCE ────────────────────────────────────────────────────
  // Condition: (393 or 933 in 13 behaviour codes) AND (9 missing from triangle)
  //            AND (3 missing from triangle OR 3 appears more than once in triangle)
  const hasImpatientCode = hasCode('393') || hasCode('933');
  const missingThree = missingFromArr(3, triangle);

  if (hasImpatientCode && missingNine && (missingThree || threesInTriangle > 1)) {
    flags.push({
      emotion: 'Extreme Impatience',
      severity: 'extreme',
      description: 'Impatience code (393/933) present, 9 absent from triangle, and 3 is either missing or duplicated — compulsive urgency pattern',
      tradingImpact: 'Severe impulsive entries, inability to wait for confirmation, chronic overtrading and premature exits',
    });
  }

  // ── HOPE ──────────────────────────────────────────────────────────────────
  // Pattern: Both 4 AND 5 missing from triangle
  const missingFive = missingFromArr(5, triangle);

  if (missingFour && missingFive) {
    flags.push({
      emotion: 'Hope',
      severity: 'moderate',
      description: 'Both 4 and 5 missing from triangle — tendency to hold on expecting reversal',
      tradingImpact: 'Holding losing positions, delaying stop-loss, expecting the market to come back',
    });
  }

  // ── LACK OF CONFIDENCE ────────────────────────────────────────────────────
  // Pattern: Number 1 missing from triangle
  const missingOne = missingFromArr(1, triangle);

  if (missingOne) {
    flags.push({
      emotion: 'Lack of Confidence',
      severity: 'mild',
      description: 'Number 1 missing from triangle — difficulty trusting own decisions',
      tradingImpact: 'Hesitation to pull the trigger, second-guessing valid setups, seeking constant validation',
    });
  }

  return flags;
}

// ─── Fire-Water Clash Detection ────────────────────────────────────────────────
// Adjacent pairs to check in triangle: A-B, A-C, B-C, B-D, C-F, C-G, D-E, D-F, E-F, F-G
// A clash = one is Fire (3 or 8) and the adjacent is Water (2 or 7)
function isFireWaterClash(a: number, b: number): boolean {
  const fire = new Set([3, 8]);
  const water = new Set([2, 7]);
  return (fire.has(a) && water.has(b)) || (water.has(a) && fire.has(b));
}

function countFireWaterClashes(p: IChingPositions): number {
  const pairs: [number, number][] = [
    [p.A, p.B], [p.A, p.C], [p.B, p.C], [p.B, p.D],
    [p.C, p.F], [p.C, p.G], [p.D, p.E], [p.D, p.F],
    [p.E, p.F], [p.F, p.G],
  ];
  return pairs.filter(([a, b]) => isFireWaterClash(a, b)).length;
}

// ─── Trader Category Assessment ────────────────────────────────────────────────
export type TraderCategory = 1 | 2 | 3;

export interface CategoryResult {
  category: TraderCategory;
  label: string;
  color: string;
  summary: string;
  correctionTimeline: string;
  redFlags: string[];
  yellowFlags: string[];
}

export function assessCategory(
  p: IChingPositions,
  codes: BehaviourCode[],
  elements: FiveElementsBox,
  emotions: EmotionFlag[]
): CategoryResult {
  const codeStrings = codes.map(c => c.code);
  const hasCode = (code: string) => codeStrings.includes(code);
  const triangle = getTriangle(p);
  const redFlags: string[] = [];
  const yellowFlags: string[] = [];

  // ── RED FLAGS (Category 3) ─────────────────────────────────────────────────

  // 1. Greed codes in any of the 13 behaviour combinations
  if (hasCode('336') || hasCode('663') || hasCode('999')) {
    redFlags.push('Greed code (336, 663, or 999) detected in behaviour patterns');
  }

  // 2. Wealth column (Children/Wealth, col index 1) < 3
  if (elements.wealthCount < 3) {
    redFlags.push(`Wealth column count is ${elements.wealthCount} — below minimum threshold of 3`);
  }

  // 3. Any zero in the 5 Elements Box (any element with count 0)
  const elementNames = ['Metal', 'Water', 'Fire', 'Wood', 'Earth'];
  elementNames.forEach(el => {
    const count = (elements as any)[el] as number;
    if (count === 0) {
      redFlags.push(`${el} element is completely absent from the 5 Elements Box`);
    }
  });

  // 4. More than 4 missing numbers in the 7-digit triangle (A–G)
  const uniqueInTriangle = new Set(triangle).size;
  const missingCount = 9 - uniqueInTriangle; // numbers 1–9 not present
  // Count how many of 1–9 are missing
  const missingNumbers = [1,2,3,4,5,6,7,8,9].filter(n => !triangle.includes(n));
  if (missingNumbers.length > 4) {
    redFlags.push(`${missingNumbers.length} numbers missing from triangle (${missingNumbers.join(', ')}) — exceeds threshold of 4`);
  }

  // 5. Extreme Fear: 4 appears more than once at A, E, G AND 9 missing from triangle
  const foursAtAEG = [p.A, p.E, p.G].filter(x => x === 4).length;
  const missingNineInTriangle = missingFromArr(9, triangle);
  if (foursAtAEG > 1 && missingNineInTriangle) {
    redFlags.push('Extreme Fear pattern: multiple 4s at key positions (A, E, G) with 9 absent');
  }

  // 6. Extreme Greed: 3 more than once in triangle + 6 missing + 336/663 in codes
  const threesInTriangle = countInArr(3, triangle);
  const missingSixInTriangle = missingFromArr(6, triangle);
  if (threesInTriangle > 1 && missingSixInTriangle && (hasCode('336') || hasCode('663'))) {
    redFlags.push('Extreme Greed pattern: multiple 3s, 6 absent, and greed code present');
  }

  // ── YELLOW FLAGS (Category 2) ──────────────────────────────────────────────

  // External Disturbance codes
  if (hasCode('257') || hasCode('527')) {
    yellowFlags.push('External disturbance code (257/527) in ABC or DEF — susceptible to outside influence');
  }
  if (hasCode('584') || hasCode('854')) {
    yellowFlags.push('External disturbance code (584/854) in CFG — susceptible to outside influence');
  }

  // Wealth Leak: Parents/Benefactor column (col index 4) < 3
  if (elements.parentsBenefactorCount < 3) {
    yellowFlags.push(`Parents/Benefactor column count is ${elements.parentsBenefactorCount} — wealth retention risk (Wealth Leak)`);
  }

  // Fire-Water Clash: has 382/832 in codes OR 4+ fire-water adjacent clashes in triangle
  const clashCount = countFireWaterClashes(p);
  if (hasCode('382') || hasCode('832')) {
    yellowFlags.push('Fire-Water clash code (382/832) present — emotional instability pattern');
    // Internal Damage sub-pattern
    const internalDamageCodes = ['988', '898', '819', '189', '393', '933'];
    const internalDamageCount = internalDamageCodes.filter(c => hasCode(c)).length;
    if (internalDamageCount >= 2) {
      yellowFlags.push('Internal Damage sub-pattern detected (2+ internal damage codes with Fire-Water clash)');
    }
  } else if (clashCount >= 4) {
    yellowFlags.push(`${clashCount} Fire-Water adjacent clashes in triangle — emotional instability pattern`);
    const internalDamageCodes = ['988', '898', '819', '189', '393', '933'];
    const internalDamageCount = internalDamageCodes.filter(c => hasCode(c)).length;
    if (internalDamageCount >= 2) {
      yellowFlags.push('Internal Damage sub-pattern detected (2+ internal damage codes with Fire-Water clashes)');
    }
  }

  // Emotional Bomb: missing ALL of 2, 3, 7, 8 from triangle
  const missingAllFireWater = [2, 3, 7, 8].every(n => missingFromArr(n, triangle));
  if (missingAllFireWater) {
    yellowFlags.push('Emotional Bomb: numbers 2, 3, 7, 8 all absent from triangle — suppressed emotional expression');
  }

  // 358/538 + missing 9
  if ((hasCode('358') || hasCode('538')) && missingNineInTriangle) {
    yellowFlags.push('Code 358/538 with 9 absent — specific emotional vulnerability pattern');
  }

  // Merits number (not naturally suited for trading)
  if (hasCode('134') || hasCode('314') || hasCode('685') || hasCode('865')) {
    yellowFlags.push('Merits number (134/314/685/865) present — charitable/religious work can help shift your luck—especially in trading.');
  }

  // Ego code
  const code13 = codes.find(c => c.id === 13);
  const isDeepEgo = code13 && (code13.code === '156' || code13.code === '516');
  if (isDeepEgo) {
    yellowFlags.push('Deep Ego code (156/516) at subconscious position — loss acceptance difficulty at core level');
  } else if (hasCode('156') || hasCode('516')) {
    yellowFlags.push('Ego code (156/516) present — difficulty accepting losses');
  }

  // Lack of Confidence: 1 missing from triangle
  if (missingFromArr(1, triangle)) {
    yellowFlags.push('Number 1 absent from triangle — lack of confidence pattern');
  }

  // Fear tendency: 4 appears more than once in triangle
  if (countInArr(4, triangle) > 1) {
    yellowFlags.push('Fear tendency: number 4 appears more than once in triangle');
  }

  // Impatience tendency: 3 more than once AND 4 missing
  if (threesInTriangle > 1 && missingFromArr(4, triangle)) {
    yellowFlags.push('Impatience tendency: multiple 3s with 4 absent — action without patience');
  }

  // Hope tendency: both 4 and 5 missing from triangle
  if (missingFromArr(4, triangle) && missingFromArr(5, triangle)) {
    yellowFlags.push('Hope tendency: both 4 and 5 absent — tendency to hold positions expecting reversal');
  }

  // Greed tendency (non-extreme)
  if (threesInTriangle > 1 && missingSixInTriangle && !hasCode('336') && !hasCode('663')) {
    yellowFlags.push('Greed tendency: multiple 3s with 6 absent (no extreme greed code)');
  }

  // External influence from Children/Subordinates (code 6 = IHJ)
  const code6 = codes.find(c => c.id === 6);
  if (code6) {
    const subordinateCodes = ['336', '663', '369', '639', '966', '696', '753'];
    if (subordinateCodes.includes(code6.code)) {
      yellowFlags.push(`Children/Subordinates code (${code6.code}) indicates external influence vulnerability`);
    }
  }

  // ── DETERMINE CATEGORY ─────────────────────────────────────────────────────
  if (redFlags.length > 0) {
    return {
      category: 3,
      label: 'Not Aligned',
      color: 'red',
      summary:
        'Significant inborn patterns that conflict with disciplined trading. Deep restructuring of mindset and approach is required before live trading.',
      correctionTimeline: '2–3 years minimum with structured coaching',
      redFlags,
      yellowFlags,
    };
  }

  if (yellowFlags.length > 0) {
    return {
      category: 2,
      label: 'Fixable with Coaching',
      color: 'amber',
      summary:
        'Correctable patterns present. With targeted awareness and coaching, these tendencies can be managed effectively.',
      correctionTimeline: 'A few months to 1 year with focused work',
      redFlags,
      yellowFlags,
    };
  }

  return {
    category: 1,
    label: 'Naturally Aligned',
    color: 'green',
    summary:
      'Your inborn patterns are well-suited for disciplined trading. Focus on refining your edge and risk management.',
    correctionTimeline: 'Minimal correction needed — focus on skill development',
    redFlags,
    yellowFlags,
  };
}

// ─── Main Character Archetype (based on G — Main Character Number) ─────────────
const MAIN_CHARACTER: Record<
  number,
  { archetype: string; traderType: string; strengths: string[]; blindspots: string[] }
> = {
  1: {
    archetype: 'The Pioneer',
    traderType: 'Trend Initiator',
    strengths: ['Independent thinking', 'Decisive action', 'Contrarian courage'],
    blindspots: ['Overconfidence', 'Ignoring consensus signals', 'Impatience with consolidation'],
  },
  2: {
    archetype: 'The Diplomat',
    traderType: 'Consensus Follower',
    strengths: ['Reads market sentiment well', 'Patient with setups', 'Manages relationships with brokers/mentors'],
    blindspots: ['Over-reliance on others\' opinions', 'Hesitation at key moments', 'Emotional sensitivity to losses'],
  },
  3: {
    archetype: 'The Communicator',
    traderType: 'News & Momentum Trader',
    strengths: ['Quick to spot opportunities', 'Optimistic mindset', 'Adapts fast to changing conditions'],
    blindspots: ['Overtrading', 'Chasing momentum', 'Greed-driven position sizing'],
  },
  4: {
    archetype: 'The Builder',
    traderType: 'Systematic Rule-Follower',
    strengths: ['Disciplined execution', 'Consistent journaling', 'Respects risk management'],
    blindspots: ['Over-analysis paralysis', 'Fear of deviation from plan', 'Slow to adapt to new conditions'],
  },
  5: {
    archetype: 'The Adventurer',
    traderType: 'Versatile Multi-Strategy Trader',
    strengths: ['Adaptable across markets', 'Curious about new strategies', 'Handles volatility well'],
    blindspots: ['Lack of consistency', 'Jumping between systems', 'Restlessness during drawdowns'],
  },
  6: {
    archetype: 'The Nurturer',
    traderType: 'Portfolio Protector',
    strengths: ['Strong risk awareness', 'Protective of capital', 'Long-term perspective'],
    blindspots: ['Over-caution misses opportunities', 'Difficulty with aggressive entries', 'Emotional attachment to positions'],
  },
  7: {
    archetype: 'The Seeker',
    traderType: 'Deep Research Analyst',
    strengths: ['Thorough analysis', 'Pattern recognition', 'Calm under pressure'],
    blindspots: ['Analysis paralysis', 'Overthinking entries', 'Isolation from market community'],
  },
  8: {
    archetype: 'The Achiever',
    traderType: 'Power Trader',
    strengths: ['Goal-oriented', 'Handles large positions', 'Strategic mindset'],
    blindspots: ['Ego-driven sizing', 'Difficulty accepting small losses', 'Revenge trading after drawdowns'],
  },
  9: {
    archetype: 'The Visionary',
    traderType: 'Macro & Big-Picture Trader',
    strengths: ['Sees long-term trends', 'Idealistic about market cycles', 'Generous with knowledge'],
    blindspots: ['Ignores short-term signals', 'Holds positions too long', 'Emotional about market injustice'],
  },
};

// ─── Full Trader Profile ───────────────────────────────────────────────────────
export interface TraderProfile {
  name: string;
  dob: string;
  positions: IChingPositions;
  mainCharacterNumber: number;
  archetype: string;
  traderType: string;
  strengths: string[];
  blindspots: string[];
  behaviourCodes: BehaviourCode[];
  fiveElements: FiveElementsBox;
  emotions: EmotionFlag[];
  category: CategoryResult;
  destinedDream: number;
  keyInsights: string[];
  // New fields
  prevailingAttitude: string;       // "C-F-G" — 3 corners of the triangle
  missingAttitudeCodes: number[];   // numbers 1–9 absent from the 7-digit triangle
  excessAttitudeCodes: number[];    // numbers appearing more than once in the triangle (with repetition)
}

export function analyseTrader(input: PersonInput): TraderProfile {
  const positions = buildPositions(input.dob);
  const behaviourCodes = buildBehaviourCodes(positions);
  const fiveElements = buildFiveElements(positions);
  const emotions = analyseEmotions(positions, behaviourCodes);
  const category = assessCategory(positions, behaviourCodes, fiveElements, emotions);

  const mainNum = positions.G;
  const charData = MAIN_CHARACTER[mainNum] || MAIN_CHARACTER[9];

  const keyInsights: string[] = [];
  keyInsights.push(`Main Character Number ${mainNum} — ${charData.archetype}: ${charData.traderType}`);
  keyInsights.push(
    `5 Elements Wealth Column (${fiveElements.columns[1]?.element}): ${fiveElements.wealthCount}/16 — ${fiveElements.wealthStatus}`
  );
  if (emotions.length === 0) {
    keyInsights.push('No dominant cognitive bias patterns detected — strong psychological foundation');
  } else {
    keyInsights.push(
      `${emotions.length} cognitive bias pattern${emotions.length > 1 ? 's' : ''} identified: ${emotions.map(e => e.emotion).join(', ')}`
    );
  }
  keyInsights.push(`Destined Dream Number: ${positions.destinedDream} — your deepest motivational driver`);

  // ── Prevailing Attitude: 3 outer corners of the triangle = A, E, G ──────────
  const prevailingAttitude = `${positions.A}-${positions.E}-${positions.G}`;

  // ── Missing Attitude Codes: numbers 1–9 not present in triangle [A,B,C,D,E,F,G] ──
  const triangle = [positions.A, positions.B, positions.C, positions.D, positions.E, positions.F, positions.G];
  const missingAttitudeCodes = [1, 2, 3, 4, 5, 6, 7, 8, 9].filter(n => !triangle.includes(n));

  // ── Excess Attitude Codes: numbers appearing more than once (listed with repetition) ──
  const excessAttitudeCodes: number[] = [];
  [1, 2, 3, 4, 5, 6, 7, 8, 9].forEach(n => {
    const cnt = triangle.filter(x => x === n).length;
    if (cnt > 1) {
      for (let i = 0; i < cnt; i++) excessAttitudeCodes.push(n);
    }
  });

  return {
    name: input.name,
    dob: input.dob,
    positions,
    mainCharacterNumber: mainNum,
    archetype: charData.archetype,
    traderType: charData.traderType,
    strengths: charData.strengths,
    blindspots: charData.blindspots,
    behaviourCodes,
    fiveElements,
    emotions,
    category,
    destinedDream: positions.destinedDream,
    keyInsights,
    prevailingAttitude,
    missingAttitudeCodes,
    excessAttitudeCodes,
  };
}

// ─── Legacy exports (kept for backward compatibility) ─────────────────────────
function CompatibilityResult(...args: any[]): any {
  console.warn('Placeholder: CompatibilityResult is not implemented.', args);
  return null;
}
export { CompatibilityResult };

function DimensionScore(...args: any[]): any {
  console.warn('Placeholder: DimensionScore is not implemented.', args);
  return null;
}
export { DimensionScore };

function NumerologyProfile(...args: any[]): any {
  console.warn('Placeholder: NumerologyProfile is not implemented.', args);
  return null;
}
export { NumerologyProfile };